import React, { useState } from 'react';
import { BookOpen, BarChart2, Users } from 'lucide-react';
import BetaAuthorDashboard from '../components/beta/AuthorDashboard';
import BookEditor from './BookEditor';
import AuthorAnalytics from './AuthorAnalytics';
import { useAuth } from '../hooks/useAuth';
import { useActiveRole } from '../hooks/useActiveRole';
import { useNavigate } from 'react-router-dom';

type Tab = 'editor' | 'beta' | 'analytics';

export default function AuthorDashboard() {
  const [activeTab, setActiveTab] = useState<Tab>('editor');
  const { user } = useAuth();
  const { activeRole, isAuthor } = useActiveRole();
  const navigate = useNavigate();

  // Redirect to reader dashboard if in reader mode
  React.useEffect(() => {
    if (activeRole === 'reader') {
      navigate('/beta/reader');
    }
  }, [activeRole, navigate]);

  if (!user || !isAuthor || activeRole === 'reader') return null;

  const tabs = [
    {
      id: 'editor' as const,
      label: 'Writing',
      icon: BookOpen,
      component: <BookEditor user={user} />,
      colors: {
        bg: 'from-rose-100 to-rose-50',
        text: 'text-rose-700',
        icon: 'text-rose-500',
        border: 'bg-rose-300'
      }
    },
    {
      id: 'beta' as const,
      label: 'Beta Readers',
      icon: Users,
      component: <BetaAuthorDashboard user={user} />,
      colors: {
        bg: 'from-violet-100 to-violet-50',
        text: 'text-violet-700',
        icon: 'text-violet-500',
        border: 'bg-violet-300'
      }
    },
    {
      id: 'analytics' as const,
      label: 'Analytics',
      icon: BarChart2,
      component: <AuthorAnalytics />,
      colors: {
        bg: 'from-emerald-100 to-emerald-50',
        text: 'text-emerald-700',
        icon: 'text-emerald-500',
        border: 'bg-emerald-300'
      }
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Tabs */}
      <div className="border-b shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 px-2 pt-2">
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative flex items-center gap-2 px-8 py-4 text-sm font-medium transition-all rounded-t-2xl
                    ${isActive 
                      ? `bg-gradient-to-b ${tab.colors.bg} ${tab.colors.text} shadow-sm`
                      : `bg-gradient-to-b ${tab.colors.bg} bg-opacity-50 hover:bg-opacity-75`
                    }
                  `}
                >
                  <tab.icon className={`w-5 h-5 transition-colors ${
                    isActive ? tab.colors.icon : 'opacity-75'
                  }`} />
                  <span className={isActive ? tab.colors.text : 'opacity-75'}>
                    {tab.label}
                  </span>
                  {isActive && (
                    <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${tab.colors.border}`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`${activeTab === 'editor' ? '' : 'max-w-7xl mx-auto px-4 py-8'}`}>
        {tabs.find(tab => tab.id === activeTab)?.component}
      </div>
    </div>
  );
}